import React, {useState, useEffect, useMemo, useContext} from 'react';
import PropTypes from 'prop-types';
import {useNavigation} from '@react-navigation/native';
import moment from 'moment';
import pickBy from 'lodash/pickBy';
import LogRocket from 'logrocket';

import UserContext from '~/components/context/UserContext';
import {
  service_request as serviceRequestApi,
  rob as robApi,
  car as carApi,
} from '~/api/private';
import {isLessorStatus} from '~/types/rob';
import {STATUS_CODES} from '~/types/statuses';
import {useDispatch, useSelector, useThrottledCallback} from '~/lib/hooks';
import {setAppDoneLoading, setAppIsLoading} from '~/actions';

import ServiceRequestContext from '../context';

const extractOverviewRobCodes = (components, replacementSlaRobCode) => [
  ...new Set(
    [
      ...components.flatMap((c) => c.tasks ?? []).map((item) => item.rob_code),
      replacementSlaRobCode,
    ].filter(Boolean),
  ),
];

const withServiceRequestContext = (Component) => {
  const WithServiceRequestContext = (props) => {
    const {damage_id} = props.route.params ?? {};

    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [editable, setEditable] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [car, setCar] = useState(null);
    const [currentMileage, setCurrentMileage] = useState(null);
    const [robComponents, setRobComponents] = useState({});
    const [invoice, setInvoice] = useState(null);
    const [vinPart, setVinPart] = useState(null);
    const [vinNumber, setVinNumber] = useState(null);
    const [robContract, setRobContract] = useState(null);
    const [hasAutoline, setHasAutoline] = useState(false);

    const [robErrors, setRobErrors] = useState([]);

    const [readLastMessage, setReadLastMessage] = useState(true);

    const dispatch = useDispatch();

    const forceRefresh = useSelector((state) => state.serviceRequestUpdate);
    const lastFetch = useSelector((state) => state.serviceRequestLastFetch);

    const navigation = useNavigation();

    const {me} = useContext(UserContext);

    const refresh = useThrottledCallback(async () => {
      try {
        const {result} = await serviceRequestApi.get(damage_id, dispatch);
        if (!result.length) {
          navigation.replace('Tabs', {
            screen: 'Calendar',
          });
          return false;
        }

        setOverview(result[0]);
        setRobErrors(result[0].rob_errors);
        setVinPart(result[0].vin_part);
        setVinNumber(result[0].vin_number);
        setCurrentMileage(result[0].mileage);
        setReadLastMessage(result[0].last_message?.is_read ?? true);

        setEditable(
          !isLessorStatus(result[0].rob?.status) &&
            result[0].status !== STATUS_CODES.Cancelled &&
            result[0].status !== STATUS_CODES.Done &&
            result[0].status !== STATUS_CODES.DoneNoInvoice &&
            result[0].status !== STATUS_CODES.Invoiced &&
            result[0].status !== STATUS_CODES.Archived,
        );
        setReadOnly(
          result[0].status === STATUS_CODES.Cancelled ||
            result[0].status === STATUS_CODES.Done ||
            result[0].status === STATUS_CODES.DoneNoInvoice ||
            result[0].status === STATUS_CODES.Invoiced ||
            result[0].status === STATUS_CODES.Archived,
        );

        const dealer = me?.dealers.find((d) => d.id === result[0].dealer_id);
        setHasAutoline(dealer?.package_access.includes('autoline'));

        await Promise.all([
          (async () => {
            const {result: contract} = await robApi.contract(
              result[0].license,
              damage_id,
            );

            setRobContract(contract);
          })(),

          (async () => {
            const rob_codes = extractOverviewRobCodes(
              result[0].components,
              result[0].replacement?.sla?.rob_code,
            );

            if (rob_codes.length) {
              const {result: robComponents} = await robApi.components(
                result[0].license,
                damage_id,
                rob_codes,
              );
              setRobComponents(robComponents);
            }
          })(),

          (async () => {
            if (
              (result[0].status === STATUS_CODES.Done ||
                result[0].status === STATUS_CODES.DoneNoInvoice ||
                result[0].status === STATUS_CODES.Invoiced ||
                result[0].status === STATUS_CODES.Archived) &&
              (result[0].rob_invoicing_status === 'ReadyForInvoicing' ||
                result[0].rob_invoicing_status === 'Invoiced')
            ) {
              const {result: invoice} = await serviceRequestApi.invoice(
                damage_id,
              );

              setInvoice(invoice);
            }
          })(),

          (async () => {
            const car = await carApi.fetch(result[0].license);
            setCar(car);
          })(),
        ]);

        return true;
      } finally {
        setLoading(false);
      }
    }, [navigation, damage_id, me, dispatch]);

    useEffect(() => {
      if (overview) {
        LogRocket.track(
          'Damage Request Details',
          pickBy({
            damage_id,
            license: overview?.license,
            status: overview?.status,
            rob_status: overview?.rob?.status,
          }),
        );
      }
    }, [damage_id, overview]);

    useEffect(() => {
      if (loading) {
        dispatch(setAppIsLoading());
      } else {
        dispatch(setAppDoneLoading());
      }
    }, [loading, dispatch]);

    useEffect(() => {
      const last_fetch_date = moment(lastFetch[damage_id] ?? new Date(0));
      const update_date = moment(
        forceRefresh[damage_id]?.timestamp ?? new Date(0),
      );
      if (update_date.isAfter(last_fetch_date, 'seconds')) {
        console.log(
          'Refreshing damage service_request',
          damage_id,
          last_fetch_date,
          update_date,
        );

        refresh();
      }
    }, [damage_id, forceRefresh, lastFetch, refresh]);

    const contextValue = useMemo(
      () => ({
        damage_id,
        loading,
        overview,
        editable,
        read_only: readOnly,
        robComponents,
        invoice,
        vin_part: vinPart,
        vin_number: vinNumber,
        robContract,
        readLastMessage,
        car,
        current_mileage: currentMileage,
        robErrors,
        has_autoline: hasAutoline,

        refresh,

        setCar,
        setCurrentMileage,
        setRobErrors,
      }),
      [
        damage_id,
        loading,
        overview,
        editable,
        readOnly,
        robComponents,
        invoice,
        vinPart,
        vinNumber,
        robContract,
        readLastMessage,
        car,
        currentMileage,
        robErrors,
        hasAutoline,
        refresh,
        setCar,
        setCurrentMileage,
        setRobErrors,
      ],
    );

    return (
      <ServiceRequestContext.Provider value={contextValue}>
        <Component {...props} />
      </ServiceRequestContext.Provider>
    );
  };

  WithServiceRequestContext.propTypes = {
    route: PropTypes.shape({
      params: PropTypes.object,
    }),
  };

  return WithServiceRequestContext;
};

export default withServiceRequestContext;
